<script setup>
import defaultEmployeeImage from "@/assets/images/user.png";
import { useAuthStore } from "@/app/stores/authStore";
import auth from "../../api/auth/auth";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";

// eslint-disable-next-line no-undef
const emit = defineEmits(['toggleSidebar'])

const open = ref(false);
const baseUrl = ref(process.env.VUE_APP_BASE_URI);
const authStore = useAuthStore();
const userPhotoLoadError = ref(false);
const dropdown = ref(null);
const trigger = ref(null);


onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside);
})

const handleClickOutside = (event) => {
  if (open.value && !dropdown.value?.contains(event.target) && !trigger.value?.contains(event.target)) {
    open.value = false;
  }
}

const user = computed(() => {
  return authStore.getUserLogged
});

const hasUser = computed(() => !!user.value);

const userPhotoUrl = computed(() => {
  if (!user.value?.photo || userPhotoLoadError.value) {
    return defaultEmployeeImage;
  }
  return baseUrl.value + user.value.photo;
});

watch(() => user.value?.photo, () => {
  userPhotoLoadError.value = false;
});

const useDefaultUserPhoto = () => {
  userPhotoLoadError.value = true;
}

const openDropdown = () => {
  open.value = !open.value;
}

const logout = () => {
  open.value = false;
  auth.logout();
}

const closeDropdown = () => {
  open.value = false;
  trigger.value?.focus();
}

const isCollaborator = computed(() => {
  return !!user.value?.roles?.includes('ROLE_EXTERNAL_COLLABORATOR');
})

const openLinkInNewTab = (link) => {
  window.open(link, '_blank');
}
const emitToggleSidebar = () => {
  emit('toggleSidebar')
}

</script>

<template>
  <nav id="navbar"
    class="navbar navbar-expand navbar-light topbar mb-4 static-top shadow d-flex justify-content-between">
    <router-link class="logo-connectus" :to="{ name: 'home' }" aria-label="Ir a Home">
      <img src="~@/assets/images/logo_connectus.png" alt="'logo ConnectUs'">
    </router-link>
    <ul class="navbar-nav ml-auto">
      <!--       link google drive-->
      <!--      <router-link class="navbar-button-links" :to="{name: 'google-drive'}">-->
      <a class="navbar-button-links" style="color:#474ce6" v-if="hasUser && !isCollaborator">
        <button type="submit" class="btn btn_white_ens btn-navbar"
          @click="openLinkInNewTab('https://grupoundanetaz.sharepoint.com/:f:/s/GrupoUndanetAZ77/EvUgcVAcFbJNs7lHMX3eiDYBs8ojalrcYQvbRY0xktgMZA?e=eHjWiy')">
          <img src="~@/assets/icons/ens-icon.svg" width="54" alt="Documentación ENS">
          <span>
            Documentación
          </span>

        </button>
      </a>

      <!--      </router-link>-->
      <router-link class="navbar-button-links" :to="{ name: 'my-benefits' }" v-if="hasUser && !isCollaborator">
        <button type="submit" class="btn btn_red btn-navbar">
          <img src="~@/assets/icons/heart-icon-white.svg" width="25" alt="Beneficios">
          Mis Beneficios
        </button>
      </router-link>
      <router-link class="navbar-button-links" :to="{ name: 'my-suggestions', params: { id: user.id } }"
        v-if="hasUser && !isCollaborator">
        <button type="submit" class="btn btn_yellow btn-navbar">
          <img src="~@/assets/icons/speaker-icon.svg" width="25" alt="Sugerencias">
          Sugerencias
        </button>
      </router-link>
      <div class="user-info" @keydown.esc.stop.prevent="closeDropdown">
        <!--                <div class="topbar-divider d-none d-sm-block"></div>-->
        <button ref="trigger" type="button" class="user-menu-trigger" aria-label="Menú de usuario"
          aria-controls="user-menu" :aria-expanded="open" @click="openDropdown">
          <span v-if="user" id="name-user" class="info-responsive">Hola {{ user.name }}</span>
          <span class="info-responsive">
          <font-awesome-icon :icon="open ? 'caret-right' : 'caret-down'" title="" style="margin-left: 5px; color: white"
            />
          </span>
          <span class="media">
          <img class="rounded-circle img-fluid thumbnail" :src="userPhotoUrl" :alt="'Employee image'"
            @error="useDefaultUserPhoto">
          </span>
        </button>

        <div v-if="open" id="user-menu" ref="dropdown" class="dropdown-content">
          <ul aria-label="Opciones de usuario">
            <li>
              <router-link class="user-menu-action" :to="{ name: 'change-password' }" @click="open = false">
                Cambiar contraseña
              </router-link>
            </li>
            <li v-if="user">
              <button type="button" class="user-menu-action" @click="logout">Salir</button>
            </li>
          </ul>
        </div>
      </div>
      <div class="navbar-display-button pointer" @click="emitToggleSidebar">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 21" fill="none">
          <path
            d="M0.857143 3.85714H23.1429C23.6163 3.85714 24 3.47341 24 3V0.857143C24 0.383732 23.6163 0 23.1429 0H0.857143C0.383732 0 0 0.383732 0 0.857143V3C0 3.47341 0.383732 3.85714 0.857143 3.85714ZM0.857143 12.4286H23.1429C23.6163 12.4286 24 12.0448 24 11.5714V9.42857C24 8.95516 23.6163 8.57143 23.1429 8.57143H0.857143C0.383732 8.57143 0 8.95516 0 9.42857V11.5714C0 12.0448 0.383732 12.4286 0.857143 12.4286ZM0.857143 21H23.1429C23.6163 21 24 20.6163 24 20.1429V18C24 17.5266 23.6163 17.1429 23.1429 17.1429H0.857143C0.383732 17.1429 0 17.5266 0 18V20.1429C0 20.6163 0.383732 21 0.857143 21Z"
            fill="white" />
        </svg>
      </div>
    </ul>
  </nav>
</template>

<style lang="scss" scoped>
#navbar {
  position: fixed;
  top: 0px;
  left: 0px;
  height: 5rem;
  width: 100%;
  padding: 0px 1.66rem;
  padding-left: 3rem;
  //border-bottom: 2px solid #6468F0;
  //background-color: #FFF;
  background-color: #6468F0;
  -webkit-box-shadow: 0 .15rem 1.75rem 0 rgba(58, 59, 69, .15) !important;
  box-shadow: 0 .15rem 1.75rem 0 rgba(58, 59, 69, .15) !important;
  z-index: 100;

  .navbar-button-links {
    margin-top: auto;
    margin-bottom: auto;
  }

  .btn-navbar {
    width: 230px;
    height: 40px;
  }

  .btn_white_ens {
    background: #FFF;
    color: #474CE6;
  }

  .btn_white_ens:hover {
    background: #D3D3D3;
    color: #5946B2;
  }

  .logo-connectus {
    display: flex;
    align-items: center;
    cursor: pointer;
    justify-content: flex-start;
  }

  .user-info {
    display: flex;
    align-items: center;
    justify-content: flex-end;

    #name-user {
      color: white;
      font-weight: bolder;
    }

    .topbar-divider {
      width: 2px;
      border-right: 1px solid #e3e6f0;
      height: calc(4.375rem - 2rem);
      margin: auto 1rem;
    }

    .thumbnail {
      width: 60px;
      height: 60px;
      padding: 0.25rem;
      background-color: #fff;
      margin: 1rem;
    }

  }

  .dropdown-content {
    width: auto;
    right: 10px;
    position: absolute;
    border-radius: 10px;
    float: none;
    top: 5.5em;
    display: block;
    left: auto;
    background-color: white;
    overflow: hidden;
    max-width: calc(100vw - 20px);
    -webkit-box-shadow: 0 .15rem 1.75rem 0 rgba(58, 59, 69, .15) !important;
    box-shadow: 0 .15rem 1.75rem 0 rgba(58, 59, 69, .15) !important;

    ul {
      margin: 0;
      padding: 0;
      list-style: none;
      display: block;
      height: auto;
      max-height: 12.66rem;

      li {
        width: 100%;
        font-size: 15px;
        display: block;
        padding: 0;
        & + li { border-top: 1px solid $light-grey-bg; }
      }
    }

    .user-menu-action {
      display: flex;
      align-items: center;
      width: 100%;
      min-height: 44px;
      margin: 0;
      padding: 0.65rem 1.5rem;
      border: 0;
      background: transparent;
      color: #858796;
      font: inherit;
      font-size: 18px;
      text-align: left;
      text-decoration: none;
      cursor: pointer;
      transition: background-color .15s ease, color .15s ease;

      &:hover, &:focus-visible {
        background: #f1f0ff;
        color: #474ce6;
        outline: none;
      }
    }
  }

  .user-menu-trigger {
    display: flex;
    align-items: center;
    margin: 0;
    padding: 0;
    border: 0;
    background: transparent;
    font: inherit;
    cursor: pointer;

    &:focus-visible {
      outline: 2px solid white;
      outline-offset: 2px;
      border-radius: 10px;
    }
  }

  .navbar-display-button {
    margin-left: 15px;
    margin-right: -14px;

    svg {
      width: 30px;
      height: calc(4.375rem + 2rem);
    }
  }
}


// Keep all shortcuts and the user menu visible on compact desktops.
@media (min-width: 993px) and (max-width: 1199px) {
  #navbar {
    padding-left: 16px;
    padding-right: 12px;
    column-gap: 12px;

    .logo-connectus img { width: 190px; }
    .navbar-nav { gap: 8px; min-width: 0; }
    .navbar-button-links { margin-left: 0; margin-right: 0; }
    .btn-navbar {
      align-items: center;
      display: inline-flex;
      justify-content: center;
      gap: 5px;
      width: 160px;
      margin: 0;
      padding: 6px 8px;
      font-size: 11px;
      img { width: 20px; }
      img[alt="Documentación ENS"] { width: 34px; }
    }
    #name-user { max-width: 110px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .user-info .thumbnail { width: 44px; height: 44px; margin: 8px; }
  }
}

@media (max-width: 992px) {
  .logo-connectus {
    margin-left: -17px;
  }

  .navbar-button-links {
    display: none;
  }

  .logo-connectus {
    img {
      width: 9rem;
    }
  }

  #navbar {
    .user-info {
      .thumbnail {
        width: 39px;
        height: 39px;
      }
    }
  }
}

@media (min-width: 992px) {
  .navbar-display-button {
    display: none;
  }
}

@media (max-width: 767px) {
  .info-responsive {
    display: none;
  }
}
</style>
